import * as React from 'react';
import { Drawer, Divider } from '@mui/material';
import { Button, IconButton } from 'components';
import { Close, FilterList } from '@mui/icons-material';

import {
  EProductsFiltersDrawerBlockType,
  TCheckboxGroupFiltersProps,
  TProductsFiltersDrawer,
  TProductsFiltersDrawerBlock,
  TRangeSliderFiltersProps,
} from './ProductsFiltersDrawer.types';
import { RangeSliderFilter } from './components/RangeSliderFilter/RangeSliderFilter';
import { CheckboxGroupFilter } from './components/CheckboxGroupFilter/CheckboxGroupFilter';
import './ProductsFiltersDrawer.styled.scss';
import { TProductFilters } from 'api/types';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export { ProductsFiltersButton } from './ProductsFiltersButton';

export const ProductsFiltersDrawer: TProductsFiltersDrawer = ({
  open,
  onClose,
  filters,
  setSelectedFilters,
  selectedFilters = {} as TProductFilters,
}) => {
  const localize = useLocalize();
  const [filtersBuffer, setFiltersBuffer] =
    React.useState<TProductFilters>(selectedFilters);

  const updateFiltersBuffer = React.useCallback(
    (filters: TProductFilters) => {
      setFiltersBuffer((previousFilters) => ({
        ...previousFilters,
        ...filters,
      }));
    },
    [setFiltersBuffer]
  );

  const handleViewResults = React.useCallback(() => {
    setSelectedFilters(Object.assign({}, filtersBuffer));
    onClose();
  }, [filtersBuffer, onClose, setSelectedFilters]);

  const handleClearAllFilters = React.useCallback(() => {
    setFiltersBuffer({});
    setSelectedFilters({});
    onClose();
  }, [onClose, setFiltersBuffer, setSelectedFilters]);

  const renderFilter = React.useCallback(
    (filter: TProductsFiltersDrawerBlock) => {
      switch (filter.blockType) {
        case EProductsFiltersDrawerBlockType.RangeSlider:
          // Hardcoded to price at the moment
          return (
            <RangeSliderFilter
              id={`${filter.id}_range_slider`}
              label={localize(filter.blockTitle) as string}
              minPrice={
                (filter.blockProps as TRangeSliderFiltersProps).minPrice
              }
              maxPrice={
                (filter.blockProps as TRangeSliderFiltersProps).maxPrice
              }
              onChange={(value) => updateFiltersBuffer({ [filter.id]: value })}
              defaultValue={selectedFilters[filter.id] as number[]}
            />
          );
        case EProductsFiltersDrawerBlockType.CheckboxGroup:
          return (
            <CheckboxGroupFilter
              options={
                (filter.blockProps as TCheckboxGroupFiltersProps).options
              }
              onChange={(value) => updateFiltersBuffer({ [filter.id]: value })}
              defaultValue={selectedFilters[filter.id] as string[]}
            />
          );
      }
    },
    [selectedFilters, localize, updateFiltersBuffer]
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      className="products-list-filters-drawer"
      PaperProps={{
        className: 'products-list-filters-drawer__paper',
      }}
    >
      <div className="products-list-filters-drawer__content">
        {/* Tabs Header */}
        <div className="products-list-filters-drawer__tabs-header">
          <div className="products-list-filters-drawer__tabs">
            <FilterList />
            <h6>{localize('products.filters.drawer.title')}</h6>
          </div>
          <IconButton
            onClick={onClose}
            className="products-list-filters-drawer__close-button"
            aria-label={
              localize('products.filters.drawer.close.aria') as string
            }
          >
            <Close />
          </IconButton>
        </div>

        {/* Body */}
        <div className="products-list-filters-drawer__body">
          {/* Price Slider Section */}
          {filters.map((filter) => (
            <div
              className="products-list-filters-drawer__section"
              key={filter.id}
            >
              <h5 className="products-list-filters-drawer__section-title">
                {localize(filter.blockTitle)}
              </h5>
              <Divider className="products-list-filters-drawer__divider" />
              {renderFilter(filter)}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="products-list-filters-drawer__footer">
          <Button
            variant="outlined"
            fullWidth
            className="products-list-filters-drawer__clear-all-button"
            onClick={handleClearAllFilters}
          >
            {localize('products.filters.clear.all')}
          </Button>
          <Button
            variant="contained"
            fullWidth
            className="products-list-filters-drawer__view-results-button"
            onClick={handleViewResults}
          >
            {localize('products.filters.view.results')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
